<script setup lang="ts">
import { Head, useForm, usePage } from "@inertiajs/vue3"
import {
  Building2,
  HelpCircle,
  Leaf,
  Mail,
  Phone,
  Plus,
  Shirt,
  User as UserIcon,
  X,
} from "lucide-vue-next"
import { computed, ref, watch } from "vue"

import InputError from "@/components/InputError.vue"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/AppLayout.vue"
import SettingsLayout from "@/layouts/settings/Layout.vue"
import { managerDashboardPath, rootPath, settingsProfilePath } from "@/routes"
import type { Auth } from "@/types"
import { type BreadcrumbItem } from "@/types"

interface EmergencyContact {
  id: string | null
  name: string
  phone: string
  relation: string
}

type ProfilePageProps = {
  company_role_info: {
    team: string
    department_director: string
    direct_manager: string
  }
  profile_data: {
    emergency_contacts: EmergencyContact[]
    dietary_preferences: {
      vegetarian: boolean
      vegan: boolean
      celiac: boolean
      diabetic: boolean
    }
    allergens: string
    dietary_details: string
    clothes_sizes: { shirt: string }
    secondary_email: string
  }
  options: {
    shirt_sizes: string[]
    relations: string[]
  }
  updated_at: string
}

const pageProps = defineProps<ProfilePageProps>()

const page = usePage()
const auth = computed(() => page.props.auth as Auth)

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const homePath =
    auth.value?.ui_mode === "manager" ? managerDashboardPath() : rootPath()

  return [
    { title: "Dashboard", href: homePath },
    { title: "Mi Perfil", href: settingsProfilePath() },
  ]
})

const user = auth.value?.user
const companyRoleInfo = pageProps.company_role_info ?? {}
const pd = pageProps.profile_data ?? {}

const form = useForm({
  emergency_contacts: (pd.emergency_contacts ?? []) as EmergencyContact[],
  dietary_preferences: {
    vegetarian: pd.dietary_preferences?.vegetarian ?? false,
    vegan: pd.dietary_preferences?.vegan ?? false,
    celiac: pd.dietary_preferences?.celiac ?? false,
    diabetic: pd.dietary_preferences?.diabetic ?? false,
  },
  clothes_sizes: {
    shirt: pd.clothes_sizes?.shirt ?? "M",
  },
  allergens: pd.allergens ?? "",
  dietary_details: pd.dietary_details ?? "",
  secondary_email: pd.secondary_email ?? "",
})

const shirtSizes = pageProps.options.shirt_sizes
const showShirtInfo = ref(false)
const relationOptions = pageProps.options.relations

function addContact() {
  form.emergency_contacts.push({ id: null, name: "", phone: "", relation: "" })
}
function removeContact(index: number) {
  form.emergency_contacts.splice(index, 1)
}

function saveChanges() {
  form.patch(settingsProfilePath(), { preserveScroll: true })
}

const hasAnyDietaryPreference = computed(() =>
  Object.values(form.dietary_preferences).some(Boolean),
)

watch(hasAnyDietaryPreference, (val) => {
  if (!val) form.dietary_details = ""
})
</script>

<template>
  <AppLayout :breadcrumbs="breadcrumbs">
    <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />
    <SettingsLayout>
      <div class="flex h-full flex-1 flex-col gap-4 rounded-xl">
        <Card class="w-full border-blue-100 bg-blue-50">
          <CardContent class="flex items-center gap-4">
            <div
              class="flex h-12 w-12 items-center justify-center rounded-full bg-blue-200 text-blue-700"
            >
              <UserIcon class="h-6 w-6" />
            </div>
            <div>
              <h1 class="text-lg font-semibold">Mi Perfil</h1>
              <p class="text-sm text-gray-500">
                Información personal y datos de empresa
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <Building2 class="h-5 w-5 text-gray-500" />
              Información de Empresa
            </CardTitle>
            <CardDescription
              >Datos proporcionados por Recursos Humanos (no
              editables)</CardDescription
            >
          </CardHeader>
          <CardContent>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div class="rounded-lg border bg-gray-50 p-4">
                <p class="text-xs text-gray-400">Nombre Completo</p>
                <p class="mt-1 font-semibold">{{ user.full_name }}</p>
              </div>
              <div class="rounded-lg border bg-gray-50 p-4">
                <p class="text-xs text-gray-400">Equipo</p>
                <p class="mt-1 font-semibold">{{ companyRoleInfo.team }}</p>
              </div>
              <div class="rounded-lg border bg-gray-50 p-4">
                <p class="text-xs text-gray-400">Director de Departamento</p>
                <p class="mt-1 font-semibold">
                  {{ companyRoleInfo.department_director }}
                </p>
              </div>
              <div class="rounded-lg border bg-gray-50 p-4">
                <p class="text-xs text-gray-400">Manager Directo</p>
                <p class="mt-1 font-semibold">
                  {{ companyRoleInfo.direct_manager }}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <!-- <UserIcon class="h-5 w-5 text-blue-500" /> -->
              Información Personal
            </CardTitle>
            <CardDescription
              >Datos editables para eventos, emergencias y
              beneficios</CardDescription
            >
          </CardHeader>
          <CardContent class="space-y-6">
            <div class="space-y-2">
              <Label class="flex items-center gap-1 font-medium">
                <Shirt class="h-4 w-4 text-purple-500" />
                Talla Camiseta/Sudadera
                <button type="button" @click="showShirtInfo = true">
                  <HelpCircle
                    class="h-3.5 w-3.5 text-gray-400 hover:text-gray-600"
                  />
                </button>
              </Label>

              <Dialog
                :open="showShirtInfo"
                @update:open="showShirtInfo = $event"
              >
                <DialogContent class="max-w-md">
                  <DialogHeader>
                    <DialogTitle class="flex items-center gap-2">
                      <Shirt class="h-5 w-5 text-purple-500" />
                      ¿Por qué necesitamos tu talla?
                    </DialogTitle>
                  </DialogHeader>
                  <div class="space-y-4 text-sm">
                    <p>
                      📦 Solicitamos tu talla de camiseta/sudadera para poder
                      preparar los
                      <strong>regalos personalizados</strong> y
                      <strong>merchandising</strong>
                      que entregamos en eventos de empresa.
                    </p>
                    <div class="rounded-xl bg-blue-50 p-4 text-blue-800">
                      <p class="mb-2 font-semibold">💡 Ejemplos:</p>
                      <ul class="list-disc space-y-1 pl-5">
                        <li>
                          Sudaderas corporativas en eventos de Team Building
                        </li>
                        <li>Camisetas conmemorativas en celebraciones</li>
                        <li>Ropa deportiva para carreras solidarias</li>
                        <li>Regalos navideños personalizados</li>
                      </ul>
                    </div>
                    <p class="text-gray-500">
                      Actualiza tu talla siempre que sea necesario para
                      asegurarte de que recibas la talla correcta.
                    </p>
                    <Button class="w-full" @click="showShirtInfo = false"
                      >Entendido</Button
                    >
                  </div>
                </DialogContent>
              </Dialog>
              <Select v-model="form.clothes_sizes.shirt">
                <SelectTrigger>
                  <SelectValue placeholder="Select a shirt size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="size in shirtSizes"
                    :key="size"
                    :value="size"
                    >{{ size }}</SelectItem
                  >
                </SelectContent>
              </Select>
              <InputError :messages="form.errors['clothes_sizes.shirt']" />
            </div>

            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <Label class="flex items-center gap-1 font-medium">
                  <Phone class="h-4 w-4 text-red-400" />
                  Contactos de Emergencia
                  <Badge variant="destructive" class="ml-1 text-xs"
                    >Obligatorio</Badge
                  >
                </Label>
                <Button variant="outline" size="sm" @click="addContact">
                  <Plus class="mr-1 h-4 w-4" />
                  Añadir
                </Button>
              </div>

              <div
                v-for="(contact, index) in form.emergency_contacts"
                :key="index"
                class="space-y-3 rounded-lg border p-4"
              >
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-600"
                    >Contacto {{ index + 1 }}</span
                  >
                  <button
                    type="button"
                    class="text-red-400 hover:text-red-600"
                    @click="removeContact(index)"
                  >
                    <X class="h-4 w-4" />
                  </button>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <Label class="mb-1 block text-xs text-gray-500"
                      >Nombre completo *</Label
                    >
                    <Input
                      v-model="contact.name"
                      placeholder="Nombre completo"
                    />
                    <InputError
                      :messages="
                        (form.errors as any)[
                          `emergency_contacts[${index}][name]`
                        ]
                      "
                    />
                  </div>
                  <div>
                    <Label class="mb-1 block text-xs text-gray-500"
                      >Teléfono *</Label
                    >
                    <Input
                      v-model="contact.phone"
                      placeholder="Teléfono"
                      type="tel"
                    />
                    <InputError
                      :messages="
                        (form.errors as any)[
                          `emergency_contacts[${index}][phone]`
                        ]
                      "
                    />
                  </div>
                  <div class="grow">
                    <Label class="mb-1 block text-xs text-gray-500"
                      >Relación *</Label
                    >
                    <Select v-model="contact.relation">
                      <SelectTrigger class="w-full">
                        <SelectValue placeholder="Select a relation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="rel in relationOptions"
                          :key="rel"
                          :value="rel"
                          >{{ rel }}</SelectItem
                        >
                      </SelectContent>
                    </Select>
                    <InputError
                      :messages="
                        (form.errors as any)[
                          `emergency_contacts[${index}][relation]`
                        ]
                      "
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <Label class="flex items-center gap-1 font-medium">
                <span class="text-orange-400">⚠</span>
                Alergias / Intolerancias
              </Label>
              <Textarea
                v-model="form.allergens"
                placeholder="Ej: Alergia a los frutos secos, intolerancia a la lactosa..."
                class="resize-none"
                rows="3"
              />
              <InputError :messages="form.errors.allergens" />
            </div>

            <div class="space-y-2">
              <Label class="flex items-center gap-1 font-medium">
                <Leaf class="h-4 w-4 text-green-500" />
                Preferencias Dietéticas
              </Label>
              <div class="flex flex-col gap-2">
                <label class="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox v-model="form.dietary_preferences.vegetarian" />
                  Vegetariano
                </label>
                <label class="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox v-model="form.dietary_preferences.vegan" />
                  Vegano
                </label>
                <label class="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox v-model="form.dietary_preferences.celiac" />
                  Celíaco (sin gluten)
                </label>
                <label class="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox v-model="form.dietary_preferences.diabetic" />
                  Diabético
                </label>
              </div>
              <div v-if="hasAnyDietaryPreference" class="space-y-1 pt-1">
                <Label class="text-xs text-gray-500"
                  >Detalles adicionales (opcional)</Label
                >
                <Textarea
                  v-model="form.dietary_details"
                  placeholder="Especifica más detalles sobre tus preferencias dietéticas..."
                  class="resize-none"
                  rows="3"
                />
                <InputError :messages="form.errors.dietary_details" />
              </div>
            </div>

            <div class="space-y-2">
              <Label class="flex items-center gap-1 font-medium">
                <Mail class="h-4 w-4 text-blue-500" />
                Correo Electrónico Corporativo Secundario
              </Label>
              <Input
                v-model="form.secondary_email"
                type="email"
                placeholder="Ej: c.serradell@comerciaglobal.com"
              />
              <InputError :messages="form.errors.secondary_email" />
              <p class="text-xs text-gray-400">
                Email corporativo alternativo para notificaciones laborales
              </p>
            </div>

            <Button
              class="w-full"
              size="lg"
              :disabled="form.processing"
              @click="saveChanges"
            >
              {{ form.processing ? "Guardando..." : "Guardar Cambios" }}
            </Button>

            <Transition
              enter-active-class="transition ease-in-out"
              enter-from-class="opacity-0"
              leave-active-class="transition ease-in-out"
              leave-to-class="opacity-0"
            >
              <p
                v-show="form.recentlySuccessful"
                class="text-center text-sm text-green-600"
              >
                Guardado correctamente.
              </p>
            </Transition>

            <p class="text-center text-xs text-gray-400">
              Última actualización: {{ $props.updated_at }}
            </p>
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  </AppLayout>
</template>
